import { ComponentProps, useLayoutEffect, useRef } from 'react';

import { useModalManager } from 'components/modal_manager/context';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { v1 as uuid } from 'uuid';

// Bootstrap Modal's onEnter/onExit expect node arguments; we redefine them as simple
// no-arg callbacks since the wrapper intercepts them for stack management.
type ModalProps = Omit<ComponentProps<typeof BootstrapModal>, 'onEnter' | 'onExit'> & {
    divider?: boolean;
    onEnter?: () => void;
    onExit?: () => void;
};

/**
 * A Wrapper extension for standard Bootstrap Modal component. Automatically adds / removes a reference to the underlying
 * component to / from the global ModalStack. Allows modals to be contextually layered in the order they were opened
 */
export const Modal = (props: ModalProps) => {

    const { backdropClassName: propBackdropClassName, className: propClassName, divider = true, onEnter, onExit, ...rest } = props;

    const { addToStack, getHeightInStack, isInStack, removeFromStack } = useModalManager();

    // Create a stable string identifier to be used in the modalStack
    const modalRef = useRef(uuid());

    // Upon unmount, check if the referenced modal is in the modalStack. If so, remove it.
    // The cleanup must be synchronous; the async removeFromStack call is intentionally fire-and-forget.
    useLayoutEffect(() => () => {

        if (isInStack(modalRef.current)) removeFromStack(modalRef.current);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Adds modal to ModalManager stack when it is "shown" and fires onEnter() prop if supplied
    const handleEnter = async () => {

        await addToStack(modalRef.current);

        // Allows onEnter() prop functionality to be passed along to underlying Modal
        if (typeof onEnter === 'function') onEnter();

    };

    // Removes modal from ModalManager stack when it is "hidden" and fires onExit() prop if supplied
    const handleExit = async () => {

        await removeFromStack(modalRef.current);

        // Allows onExit() prop functionality to be passed along to underlying Modal
        if (typeof onExit === 'function') onExit();

    };

    // Find the depth at which to render the backdrop and generate the proper className
    const depth = getHeightInStack(modalRef.current);
    const backdropClassName = `stackable-modal-backdrop stack-height-${depth} ${propBackdropClassName || ''}`;
    const modalClassName = `stackable-modal stack-height-${depth} ${!divider ? 'remove-title-divider' : ''} ${propClassName || ''}`;

    return (
        <BootstrapModal
            {...rest}
            backdropClassName={backdropClassName}
            className={modalClassName}
            onEntered={handleEnter}
            onExit={handleExit}
        />
    );

};

// Attach the standard Bootstrap Modal sub-components to the wrapper for ease of implementation
Modal.Body = BootstrapModal.Body;
Modal.Dialog = BootstrapModal.Dialog;
Modal.Footer = BootstrapModal.Footer;
Modal.Header = BootstrapModal.Header;
Modal.Title = BootstrapModal.Title;

Modal.displayName = 'ManagedModal';